import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

type Quebra = "sm" | "md" | "nunca";

type Props = {
  segmentacao: ReactNode;
  acoes: ReactNode;
  /** Onde as ações descem pra linha de baixo. `nunca` = cabem junto mesmo em 390px. */
  quebra?: Quebra;
  className?: string;
};

/**
 * Linha de segmentação + ações. A regra: cabendo na linha, fica na linha; não cabendo, as ações
 * descem e preenchem a largura.
 *
 * Cheguei a tentar decidir isso pelo espaço real do contêiner, com `flex-basis` calculado sobre um
 * limiar. Funciona, mas o limiar continua sendo um número escolhido à mão por tela — ou seja, é um
 * breakpoint disfarçado — e brigava com o "preencher a largura ao quebrar". Breakpoint declarado
 * entrega o mesmo resultado e qualquer um entende ao ler. Quem decide é a página, que é quem sabe
 * o tamanho dos próprios rótulos.
 */
export function FilterBar({ segmentacao, acoes, quebra = "sm", className }: Props) {
  const juntos = {
    sm: "sm:flex-nowrap",
    md: "md:flex-nowrap",
    nunca: "flex-nowrap",
  }[quebra];

  const larguraSegmentacao = {
    sm: "w-full sm:w-auto sm:flex-1",
    md: "w-full md:w-auto md:flex-1",
    nunca: "min-w-0 flex-1",
  }[quebra];

  const larguraAcoes = {
    sm: "w-full sm:ml-auto sm:w-auto",
    md: "w-full md:ml-auto md:w-auto",
    nunca: "ml-auto",
  }[quebra];

  return (
    <div className={cn("flex flex-wrap items-center gap-2", juntos, className)}>
      <div className={cn("min-w-0", larguraSegmentacao)}>{segmentacao}</div>
      <div className={cn("flex shrink-0 items-center gap-2", larguraAcoes)}>{acoes}</div>
    </div>
  );
}
